import { useState } from 'react';
import { Space } from 'antd';
import moment from 'moment';
import Drawer from 'components/ui/base/drawer/Drawer';
import { ICS2ArrivalNotificationMessage } from 'store/declarations/eu/ics2/arrival/ics2-arrival-notification-declaration';
import { getIcs2NotificationMessageLabel } from 'store/declarations/eu/ics2/ics2-notification';
import NotificationsList from 'views/declarations/common/declaration-errors-drawer/declaration-notifications/ics2/components/NotificationsList';
import IE3N99Details from './messages/IE3N99Details';
import ArrivalMessageDetails from './ArrivalMessageDetails';

const DATE_FORMAT = 'DD/MM/YYYY, HH:mm';

export type ArrivalNotifications =
    | { kind: 'errors'; rejection: ICS2ArrivalNotificationMessage }
    | { kind: 'messages'; messages: ICS2ArrivalNotificationMessage[] };

const titles: Record<ArrivalNotifications['kind'], string> = {
    errors: 'Arrival notification errors',
    messages: 'Messages',
};

type Props = {
    notifications: ArrivalNotifications;
    open: boolean;
    onClose: () => void;
};

const ArrivalNotificationsDrawer = ({ notifications, open, onClose }: Props) => {
    const [selected, setSelected] = useState<ICS2ArrivalNotificationMessage | undefined>();

    const handleClose = () => {
        setSelected(undefined);
        onClose();
    };

    const content = () => {
        if (notifications.kind === 'errors') return <IE3N99Details message={notifications.rejection} />;

        if (selected) return <ArrivalMessageDetails message={selected} onBack={() => setSelected(undefined)} />;

        return (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {notifications.messages.map((message) => (
                    <NotificationsList
                        key={message.messageUuid}
                        code={message.messageType ?? '-'}
                        description={getIcs2NotificationMessageLabel(message.messageType) ?? 'Customs message'}
                        location={message.receivedAt && `Received ${moment(message.receivedAt).format(DATE_FORMAT)}`}
                        onSeeDetails={() => setSelected(message)}
                    />
                ))}
            </Space>
        );
    };

    return (
        <Drawer
            open={open}
            onClose={handleClose}
            width={820}
            warning={notifications.kind === 'errors'}
            caution={notifications.kind !== 'errors'}
            title={titles[notifications.kind]}
        >
            {content()}
        </Drawer>
    );
};

export default ArrivalNotificationsDrawer;
